import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { StaffFormDialog } from "@/components/StaffFormDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { getStaffById, deleteStaff, fullName, initials } from "@/lib/api";
import { Phone, Mail, MapPin, Cake, IdCard, Users, Building2, Globe, Facebook, Instagram, Twitter, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const toMinutes = (t) => {
  if (!t) return null;
  const m = t.trim().match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ap = m[3] ? m[3].toLowerCase() : null;
  if (ap === "pm" && h !== 12) h += 12;
  else if (ap === "am" && h === 12) h = 0;
  else if (!ap) h = h >= 8 ? h : h + 12;
  return h * 60 + min;
};

const currentPeriodIndex = (schedule) => {
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const starts = schedule.map((p) => toMinutes(p.time));
  for (let i = 0; i < schedule.length; i++) {
    const start = starts[i];
    const end = i + 1 < schedule.length ? starts[i + 1] : (start != null ? start + 50 : null);
    if (start != null && end != null && nowMin >= start && nowMin < end) return i;
  }
  return -1;
};

export const StaffDetailModal = ({ staff: initial, open, onOpenChange, onChanged }) => {
  const { isAdmin } = useAuth();
  const [staff, setStaff] = useState(initial);
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  useEffect(() => { setStaff(initial); }, [initial]);

  if (!staff) return null;

  const reload = async () => {
    try { setStaff(await getStaffById(staff.id)); } catch { /* deleted */ }
    onChanged?.();
  };

  const onDelete = async () => {
    await deleteStaff(staff.id);
    toast.success("Staff member deleted");
    setDelOpen(false);
    onOpenChange(false);
    onChanged?.();
  };

  const curIdx = currentPeriodIndex(staff.schedule || []);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="staff-detail-modal"
          className="rounded-none border border-[#0A1F44] bg-[#F3F5F8] max-w-3xl max-h-[90vh] overflow-y-auto p-0"
        >
          <div className="p-6 sm:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-6">
              <div className="sm:col-span-4">
                <div className="aspect-[3/4] max-w-[180px] bg-[#0A1F44]/5 overflow-hidden border border-[#0A1F44]/20 flex items-center justify-center">
                  {staff.avatar_url ? (
                    <img src={staff.avatar_url} alt={fullName(staff)} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
                  ) : (
                    <span className="font-serif text-4xl text-[#0A1F44]/40">{initials(staff)}</span>
                  )}
                </div>
              </div>

              <div className="sm:col-span-8">
                <div className="text-xs uppercase tracking-[0.25em] text-[#C0982E] mb-2" data-testid="modal-department">
                  {staff.department || "Faculty"}{staff.department2 && staff.department2.trim().toLowerCase() !== (staff.department || "").trim().toLowerCase() ? ` / ${staff.department2}` : ""}
                </div>
                <h1 data-testid="modal-name" className="font-serif text-4xl sm:text-5xl text-[#0A1F44] leading-none">
                  {fullName(staff)}
                </h1>

                {isAdmin && (
                  <div className="flex gap-2 mt-4">
                    <Button
                      data-testid="modal-edit-btn"
                      onClick={() => setEditOpen(true)}
                      variant="outline"
                      className="rounded-none border-[#C0982E] text-[#C0982E] hover:bg-[#C0982E] hover:text-[#0A1F44] gap-2 text-xs uppercase tracking-wider transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </Button>
                    <Button
                      data-testid="modal-delete-btn"
                      onClick={() => setDelOpen(true)}
                      className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E] hover:text-[#0A1F44] gap-2 text-xs uppercase tracking-wider transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 border-l border-t border-[#0A1F44]/20">
              <InfoCell icon={Phone} label="Phone" value={staff.phone} testid="modal-phone" href={staff.phone ? `tel:${staff.phone}` : null} />
              <InfoCell icon={Mail} label="Email" value={staff.email} testid="modal-email" href={staff.email ? `mailto:${staff.email}` : null} />
              <InfoCell icon={Building2} label="School Campus" value={staff.school_campus} testid="modal-campus" />
              <InfoCell icon={MapPin} label="Room" value={staff.room ? `Rm: ${staff.room}` : ""} testid="modal-room" />
              <InfoCell icon={Cake} label="Birthday" value={staff.birthday} testid="modal-birthday" />
              <InfoCell icon={IdCard} label="Staff ID" value={staff.staff_id} testid="modal-staffid" />
              <InfoCell icon={Users} label="Admin Supervisor" value={staff.department_head} testid="modal-depthead" />
            </div>

            {(staff.website || staff.facebook || staff.instagram || staff.x_handle) && (
              <div className="mt-8" data-testid="modal-connect">
                <h2 className="font-serif text-2xl text-[#0A1F44] mb-4">Connect</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 border-l border-t border-[#0A1F44]/20">
                  <SocialCell icon={Globe} label="Website" value={staff.website} kind="website" testid="modal-website" />
                  <SocialCell icon={Facebook} label="Facebook" value={staff.facebook} kind="facebook" testid="modal-facebook" />
                  <SocialCell icon={Instagram} label="Instagram" value={staff.instagram} kind="instagram" testid="modal-instagram" />
                  <SocialCell icon={Twitter} label="X Handle" value={staff.x_handle} kind="x" testid="modal-x" />
                </div>
              </div>
            )}

            <div className="mt-8">
              <h2 className="font-serif text-2xl text-[#0A1F44] mb-1">Daily Schedule</h2>
              <p className="text-sm text-[#0A1F44]/50 mb-4">
                {curIdx >= 0 ? "Current period highlighted in gold." : "No class in session right now."}
              </p>
              <table data-testid="modal-schedule" className="w-full border-collapse border border-[#0A1F44]/20 text-sm">
                <thead>
                  <tr className="bg-[#E6EAF1]">
                    <th className="border border-[#0A1F44]/20 p-3 text-left text-xs uppercase tracking-wider">Period</th>
                    <th className="border border-[#0A1F44]/20 p-3 text-left text-xs uppercase tracking-wider">Time</th>
                    <th className="border border-[#0A1F44]/20 p-3 text-left text-xs uppercase tracking-wider">Class</th>
                  </tr>
                </thead>
                <tbody>
                  {(staff.schedule || []).map((p, i) => (
                    <tr key={i} className={i === curIdx ? "bg-[#C0982E] text-[#F3F5F8]" : ""}>
                      <td className="border border-[#0A1F44]/20 p-3 font-medium">{p.period}</td>
                      <td className="border border-[#0A1F44]/20 p-3">{p.time}</td>
                      <td className="border border-[#0A1F44]/20 p-3 font-serif text-base">{p.subject}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <StaffFormDialog open={editOpen} onOpenChange={setEditOpen} existing={staff} onSaved={reload} />

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="rounded-none border border-[#0A1F44] bg-[#F3F5F8]" data-testid="modal-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete {fullName(staff)}?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove this staff member from the directory.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#0A1F44]" data-testid="modal-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="rounded-none bg-[#b3402a] hover:bg-[#8f3221] text-white" data-testid="modal-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const InfoCell = ({ icon: Icon, label, value, testid, href }) => (
  <div className="border-r border-b border-[#0A1F44]/20 p-4">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#0A1F44]/50 mb-1">
      <Icon className="w-3 h-3" strokeWidth={1.5} /> {label}
    </div>
    {href && value ? (
      <a href={href} data-testid={testid} className="text-[#0A1F44] hover:text-[#C0982E] transition-colors break-all">{value}</a>
    ) : (
      <div data-testid={testid} className="text-[#0A1F44] break-all">{value || "—"}</div>
    )}
  </div>
);

const socialHref = (kind, value) => {
  const v = (value || "").trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  const handle = v.replace(/^@/, "").trim();
  if (/\s/.test(v) || /^(n\/?a|none|na)$/i.test(v)) return null;
  if (kind === "instagram") return `https://instagram.com/${handle}`;
  if (kind === "x") return `https://x.com/${handle}`;
  if (kind === "website") return `https://${v}`;
  return null;
};

const SocialCell = ({ icon: Icon, label, value, kind, testid }) => {
  const href = socialHref(kind, value);
  return (
    <div className="border-r border-b border-[#0A1F44]/20 p-4">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#0A1F44]/50 mb-1">
        <Icon className="w-3 h-3" strokeWidth={1.5} /> {label}
      </div>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className="text-[#0A1F44] hover:text-[#C0982E] transition-colors break-all">{value}</a>
      ) : (
        <div data-testid={testid} className="text-[#0A1F44] break-all">{value || "—"}</div>
      )}
    </div>
  );
};
